// Fob (NFC card) enrollment walk-through: pick a reader (or use the
// building's pinned enrollment reader), open a tap window, auto-issue the
// credential once the fob is detected. Shared between the employee detail
// page's standalone "Enroll fob" action and the "enroll fob now?" step
// offered right after an employee is created.
//
// The tap window itself is handed off to EnrollmentFeedback — the same
// full-attention "scan now" popup with a live countdown used by the
// standalone /credentials/enroll page — so every fob-enrollment surface in
// the app looks and behaves the same way.
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import { Button } from '@ng-unifi/ui';
import { apiClient, ApiError, type ApiRequestInit } from '../lib/api.js';
import { useIdempotencyKey } from '../lib/useIdempotencyKey.js';
import { statusLabel } from '../lib/humanLabels.js';
import { Field, ModalShell } from './Modal.js';
import { EnrollmentFeedback, type EnrollmentPhase } from './EnrollmentFeedback.js';

interface EnrollmentReaderOption {
  id: string;
  name: string;
  label_override?: string | null;
  kind: string;
  status: string;
}

const ENROLLMENT_TTL_SECONDS = 120;

export function EnrollFobModal({
  empId,
  personName,
  thirdPartyNfcEnabled,
  enrollmentReaderMode,
  enrollmentReaderId,
  enrollmentReaderName,
  scopeOpts,
  onCancel,
  onDone,
}: {
  empId: string;
  /** Employee name, surfaced on the enrollment popup's success screen. */
  personName?: string | null;
  thirdPartyNfcEnabled: boolean;
  enrollmentReaderMode: string;
  enrollmentReaderId: string | null;
  enrollmentReaderName: string | null;
  scopeOpts?: ApiRequestInit;
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  const { key: startKey } = useIdempotencyKey();
  const { key: completeKey } = useIdempotencyKey();
  const [step, setStep] = useState<'pick' | 'waiting'>('pick');
  const [readerId, setReaderId] = useState(
    enrollmentReaderMode === 'specified' && enrollmentReaderId ? enrollmentReaderId : '',
  );
  const [label, setLabel] = useState('');
  const [enrollment, setEnrollment] = useState<{
    enrollment_id: string;
    expires_in_sec: number;
    status: 'waiting' | 'detected';
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Drives the EnrollmentFeedback popup once the tap window is open.
  const [phase, setPhase] = useState<EnrollmentPhase>('waiting');
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const expiresAtRef = useRef<number | null>(null);
  const [detected, setDetected] = useState(false);
  // Reader options come from tenant-granted devices, or from the building
  // resource pool for master admins. Empty means enrollment is blocked until
  // hardware resources are refreshed or a policy reader is pinned.
  const [availableReaders, setAvailableReaders] = useState<EnrollmentReaderOption[] | null>(null);
  // All granted devices keyed by id, for friendly-name lookup regardless of kind filter.
  const [deviceNameMap, setDeviceNameMap] = useState<Map<string, string> | null>(null);

  const readerDisplayName = useCallback(
    (id: string) => {
      const configuredName =
        enrollmentReaderMode === 'specified' &&
        id === enrollmentReaderId &&
        enrollmentReaderName?.trim()
          ? enrollmentReaderName.trim()
          : null;
      return configuredName ?? deviceNameMap?.get(id) ?? id;
    },
    [deviceNameMap, enrollmentReaderId, enrollmentReaderMode, enrollmentReaderName],
  );

  const autoCompleteRef = useRef(false);

  const beginEnrollment = useCallback(
    async (reader: string) => {
      if (reader.trim().length === 0) {
        setError('Reader is required.');
        return;
      }
      setSubmitting(true);
      setError(null);
      setEnrollError(null);
      try {
        const res = await apiClient.withIdempotencyKey(startKey).post<{
          data: {
            enrollment_id: string;
            expires_in_sec: number;
            status: 'waiting' | 'detected';
          };
        }>(
          `/api/v1/employees/${empId}/credentials/nfc-enrollments`,
          {
            reader_id: reader.trim(),
            ttl_seconds: ENROLLMENT_TTL_SECONDS,
            ...(label.trim() ? { label: label.trim() } : {}),
          },
          scopeOpts,
        );
        expiresAtRef.current = Date.now() + res.data.expires_in_sec * 1000;
        setSecondsLeft(res.data.expires_in_sec);
        setDetected(false);
        autoCompleteRef.current = false;
        setEnrollment(res.data);
        setPhase('waiting');
        setStep('waiting');
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Failed to start enrollment.');
      } finally {
        setSubmitting(false);
      }
    },
    [empId, label, scopeOpts, startKey],
  );

  function startEnrollment(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    void beginEnrollment(readerId);
  }

  // Building owners can pin a single dedicated enrollment reader in
  // Credential policy. When they have, skip the picker/"Start enrollment"
  // click entirely and jump straight to the tap step — the reader is
  // already known, so there's nothing left for the operator to decide.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (autoStartedRef.current) return;
    if (step !== 'pick' || !thirdPartyNfcEnabled) return;
    if (enrollmentReaderMode !== 'specified' || !enrollmentReaderId) return;
    autoStartedRef.current = true;
    void beginEnrollment(enrollmentReaderId);
  }, [beginEnrollment, enrollmentReaderId, enrollmentReaderMode, step, thirdPartyNfcEnabled]);

  const completeEnrollment = useCallback(async () => {
    if (!enrollment || phase === 'issuing') return;
    setPhase('issuing');
    setEnrollError(null);
    try {
      await apiClient
        .withIdempotencyKey(completeKey)
        .post(
          `/api/v1/employees/${empId}/credentials/nfc-enrollments/${enrollment.enrollment_id}/complete`,
          label.trim() ? { label: label.trim() } : {},
          scopeOpts,
        );
      setPhase('success');
    } catch (err) {
      setEnrollError(
        err instanceof ApiError
          ? err.message
          : 'We couldn’t finish adding the fob. Please try again.',
      );
      setPhase('error');
    }
  }, [completeKey, empId, enrollment, label, phase, scopeOpts]);

  // Auto-complete: fire as soon as the fob is detected.
  useEffect(() => {
    if (!detected || phase !== 'waiting' || autoCompleteRef.current) return;
    autoCompleteRef.current = true;
    void completeEnrollment();
  }, [detected, phase, completeEnrollment]);

  // Live countdown for the tap window; flips to "timed out" at zero and
  // frees the reader so it doesn't sit listening after the operator's
  // attention has moved on.
  useEffect(() => {
    if (step !== 'waiting' || phase !== 'waiting' || expiresAtRef.current == null) return undefined;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((expiresAtRef.current! - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        setPhase('expired');
        const session = enrollment;
        if (session) {
          void apiClient
            .delete(
              `/api/v1/employees/${empId}/credentials/nfc-enrollments/${session.enrollment_id}`,
              scopeOpts,
            )
            .catch(() => undefined);
        }
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [empId, enrollment, phase, scopeOpts, step]);

  // Celebrate briefly, then hand off to the caller so the modal closes.
  useEffect(() => {
    if (phase !== 'success') return undefined;
    const id = setTimeout(() => {
      onDone('Fob detected at the reader and credential issued.');
    }, 2800);
    return () => clearTimeout(id);
  }, [phase, onDone]);

  // Load the reader picklist once when the modal opens. Load regardless of
  // step so the friendly name is available in the popup too.
  useEffect(() => {
    if (availableReaders !== null) return;
    let cancelled = false;
    const readerRequest = thirdPartyNfcEnabled
      ? apiClient
          .get<{ data: EnrollmentReaderOption[] }>(
            '/api/v1/employees/credentials/enrollment-readers',
            scopeOpts,
          )
          .then((res) => res.data)
      : Promise.resolve([] as EnrollmentReaderOption[]);

    readerRequest
      .then((res) => {
        if (cancelled) return;
        setDeviceNameMap(new Map(res.map((d) => [d.id, d.label_override || d.name || d.id])));
        setAvailableReaders(res);
        if (enrollmentReaderMode === 'specified' && enrollmentReaderId) {
          setReaderId(enrollmentReaderId);
        } else if (res.length > 0 && step === 'pick') {
          // Only clear the selected reader in the pick step. If we're
          // already waiting, keep the id so the display name resolves.
          setReaderId('');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setAvailableReaders([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    availableReaders,
    enrollmentReaderId,
    enrollmentReaderMode,
    scopeOpts,
    step,
    thirdPartyNfcEnabled,
  ]);

  // Poll the reader while the popup is listening. Transient failures are
  // normal while the reader is idle — keep listening rather than surfacing
  // noise to the operator.
  useEffect(() => {
    if (step !== 'waiting' || phase !== 'waiting' || !enrollment) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await apiClient.get<{
          data: {
            status: 'waiting' | 'detected';
            token_present: boolean;
            card_id: string | null;
          };
        }>(
          `/api/v1/employees/${empId}/credentials/nfc-enrollments/${enrollment.enrollment_id}`,
          scopeOpts,
        );
        if (cancelled) return;
        setDetected(res.data.token_present);
      } catch {
        // Transient poll failures are normal while the reader is idle.
      }
      if (!cancelled) timer = setTimeout(poll, 2000);
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [empId, enrollment, phase, scopeOpts, step]);

  // Abandon a scan that's still listening and free the reader.
  function cancelEnrollment() {
    const session = enrollment;
    if (session) {
      void apiClient
        .delete(
          `/api/v1/employees/${empId}/credentials/nfc-enrollments/${session.enrollment_id}`,
          scopeOpts,
        )
        .catch(() => undefined);
    }
    onCancel();
  }

  // Restart the tap window against the same reader after a timeout or error.
  function retryEnrollment() {
    setEnrollment(null);
    setDetected(false);
    autoCompleteRef.current = false;
    void beginEnrollment(readerId);
  }

  function closeFeedback() {
    if (phase === 'success') {
      onDone('Fob detected at the reader and credential issued.');
    } else {
      onCancel();
    }
  }

  return (
    <>
      {step === 'pick' ? (
        <ModalShell title="Enroll fob" onCancel={onCancel} testId="enroll-fob-modal">
          <form onSubmit={startEnrollment} className="space-y-4">
            <p className="font-body-md text-body-md text-on-surface-variant">
              {enrollmentReaderMode === 'specified' && enrollmentReaderId
                ? 'Start an enrollment window and present the fob to the dedicated enrollment reader when prompted.'
                : 'Pick a reader and start an enrollment window. Present the fob to the reader when prompted; the credential will issue automatically.'}
            </p>

            {enrollmentReaderMode === 'specified' && enrollmentReaderId ? (
              <div className="rounded border border-outline-variant bg-surface-container-lowest px-3 py-2">
                <p className="font-label-md text-label-md text-on-surface-variant uppercase text-xs mb-1">
                  Reader
                </p>
                <p className="font-body-md text-body-md text-on-surface">
                  {readerDisplayName(enrollmentReaderId)}
                </p>
                <p className="font-body-sm text-body-sm text-on-surface-variant mt-0.5">
                  Dedicated enrollment reader configured by admin.
                </p>
                <input type="hidden" value={readerId} data-testid="enroll-reader-input" readOnly />
              </div>
            ) : (
              <Field label="Reader" htmlFor="enroll-reader">
                {availableReaders && availableReaders.length > 0 ? (
                  <>
                    <select
                      id="enroll-reader"
                      value={readerId}
                      onChange={(e) => setReaderId(e.target.value)}
                      required
                      className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
                      data-testid="enroll-reader-select"
                    >
                      <option value="" disabled>
                        Pick a reader…
                      </option>
                      {availableReaders.map((r) => (
                        <option key={r.id} value={r.id} disabled={r.status === 'offline'}>
                          {r.label_override || r.name || r.id} - {r.kind}
                          {r.status !== 'online' ? ` (${statusLabel(r.status)})` : ''}
                        </option>
                      ))}
                    </select>
                  </>
                ) : availableReaders === null ? (
                  <p className="mt-1 font-body-md text-body-md text-on-surface-variant">
                    Loading discovered readers…
                  </p>
                ) : (
                  <>
                    <select
                      id="enroll-reader"
                      value=""
                      onChange={() => undefined}
                      disabled
                      className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
                      data-testid="enroll-reader-select"
                    >
                      <option value="">No enrollment reader devices found</option>
                    </select>
                    <p className="mt-1 font-body-md text-body-md text-on-surface-variant">
                      No enrollment readers are available. A super admin can refresh hardware
                      resources or set a site enrollment reader in Credential policy.
                    </p>
                  </>
                )}
              </Field>
            )}
            <Field label="Label (optional)" htmlFor="enroll-label">
              <input
                id="enroll-label"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={120}
                className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
              />
            </Field>
            <input type="hidden" value={startKey} data-testid="enroll-idempotency-key" readOnly />
            {error ? (
              <div role="alert" className="text-error font-body-md text-body-md">
                {error}
              </div>
            ) : null}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" type="button" onClick={onCancel} disabled={submitting}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={submitting || readerId.trim().length === 0}
                data-testid="enroll-start-btn"
              >
                {submitting ? 'Starting…' : 'Start enrollment'}
              </Button>
            </div>
          </form>
        </ModalShell>
      ) : null}
      <EnrollmentFeedback
        open={step === 'waiting'}
        phase={phase}
        variant="scan"
        readerName={readerId ? readerDisplayName(readerId) : null}
        personName={personName ?? null}
        credentialLabel="fob"
        secondsLeft={secondsLeft}
        errorMessage={enrollError}
        onCancel={cancelEnrollment}
        onRetry={retryEnrollment}
        onClose={closeFeedback}
      />
    </>
  );
}
